import { isNullOrUndefined } from 'util';
import { Map, Set } from 'immutable';

import { EisenhowerElement } from '../interface/eisenhower.interface';
import { NODES_TYPE_CATEGORYGROUP, NODES_TYPE_STRUCTURE } from '../../../shared/api/nodes/nodes.models';
import { CoreMultiTransfer, TreeNode } from '../../../core/interface/core.interface';
import { CoreUtilities } from '../../../core/utilities/core.utilities';
import { Injectable } from '@angular/core';

@Injectable()
export class EisenhowerService {

  public folders: TreeNode[];

  public yAxis: TreeNode;
  public xAxis: TreeNode;
  public matrix = [];
  public notAssignedFolders: TreeNode[] = [];

  public treeNodes: TreeNode[] = [];
  public unfilteredTreeNodes: TreeNode[] = [];

  public preSorting = {};

  public modelId: string;

  private categoryMatrix = Map<string, EisenhowerElement>();
  private parentNodeType: number;

  public constructor(private coreUtilities: CoreUtilities) { }

  public setParentNodeType(nodeType) {
    this.parentNodeType = nodeType;
    return this;
  }

  public getPriorityScore(treeNode: TreeNode): number {
    let priorityScore = 0;
    if (!isNullOrUndefined(this.xAxis) && !isNullOrUndefined(this.yAxis) && !isNullOrUndefined(this.xAxis.formFieldId) && this.xAxis.formFieldId !== '' && !isNullOrUndefined(this.yAxis.formFieldId) && this.yAxis.formFieldId !== '') {
      // const xy = treeNode[this.xAxis.formFieldId] + ':' + treeNode[this.yAxis.formFieldId];
      const xy = this.getXY(undefined, this.xAxis, treeNode) + ':' + this.getXY(undefined, this.yAxis, treeNode);
      if (this.categoryMatrix.has(xy)) {
        const category = this.categoryMatrix.get(xy).category;
        if (!isNullOrUndefined(category)) {
          priorityScore = category.sorting;
        }
      }
    }
    return priorityScore;
  }

  public setMatrix(fieldNodes: TreeNode[]) {
    /* Set the axis */
    this.yAxis = fieldNodes[0];
    this.xAxis = fieldNodes[1];
    /* Replace children in case of structural setup */
    if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
      let nodeTypes = Set<number>();
      if (this.yAxis.filteredChildren === undefined) {
        nodeTypes = Set<number>(this.yAxis.children.map(child => child.nodeType));
        this.yAxis.filteredChildren = this.yAxis.children;
      } else {
        nodeTypes = Set<number>(this.yAxis.filteredChildren.map(child => child.nodeType));
      }
      this.yAxis.children = this.unfilteredTreeNodes.filter(treeNode => nodeTypes.has(treeNode.nodeType));
    }
    if (this.xAxis.nodeType === NODES_TYPE_STRUCTURE) {
      let nodeTypes = Set<number>();
      if (this.xAxis.filteredChildren === undefined) {
        nodeTypes = Set<number>(this.xAxis.children.map(child => child.nodeType));
        this.xAxis.filteredChildren = this.xAxis.children;
      } else {
        nodeTypes = Set<number>(this.xAxis.filteredChildren.map(child => child.nodeType));
      }
      this.xAxis.children = this.unfilteredTreeNodes.filter(treeNode => nodeTypes.has(treeNode.nodeType));
    }
    /* Set the matrix */
    const matrix = [];

    /* Sort the children */
    if (!isNullOrUndefined(this.xAxis)) {
      this.xAxis.children = this.sortChildren(this.xAxis.children);
    }
    if (!isNullOrUndefined(this.yAxis)) {
      this.yAxis.children = this.sortChildren(this.yAxis.children);
    }

    if (isNullOrUndefined(this.xAxis) || isNullOrUndefined(this.yAxis)) {
      return;
    }

    /* Count */
    const countX = this.xAxis.children.length;
    const countY = this.yAxis.children.length;
    /* Iterate over elements */
    for (let i = 0; i < countX; i++) {
      const x = [];
      for (let j = 0; j < countY; j++) {
        /* Set the matrix element */
        const element = <EisenhowerElement>{ y: this.yAxis.children[j], x: this.xAxis.children[i], category: undefined, treeNodes: [] };
        /* Get the category nodes */
        const xCategories = element.x.children.filter(child => child.nodeType === NODES_TYPE_CATEGORYGROUP);
        const yCategories = element.y.children.filter(child => child.nodeType === NODES_TYPE_CATEGORYGROUP);
        /* Counts */
        const countCategoriesX = xCategories.length;
        const countCategoriesY = yCategories.length;
        /* Set the category */
        for (let k = 0; k < countCategoriesY; k++) {
          const yCategory = yCategories[k];
          for (let lx = 0; lx < countCategoriesX; lx++) {
            const xCategory = xCategories[lx];
            if (yCategory.id === xCategory.id) {
              element.category = yCategory;
            }
          }
        }
        /* Store it */
        if (!isNullOrUndefined(this.xAxis.formFieldId) && this.xAxis.formFieldId !== '' && !isNullOrUndefined(this.yAxis.formFieldId) && this.yAxis.formFieldId !== '') {
          this.categoryMatrix = this.categoryMatrix.set(this.getXY(element.x, this.xAxis, undefined) + ':' + this.getXY(element.y, this.yAxis, undefined), element);
        }
        /* Add the matrix element */
        x.push(element);
      }
      matrix.push(x);
    }
    /* Set the matrix */
    this.matrix = matrix;
  }

  public assignTreeNodes(treeNodes: TreeNode[]) {
    this.clearMatrix();

    const parentIds = this.folders !== undefined ? this.folders.map(folder => folder.id) : [];

    let parents = Map<string, TreeNode>();
    let assigned = Set<string>();
    let count = treeNodes.length;
    for (let i = 0; i < count; i++) {
      const treeNode = treeNodes[i];
      /* Filter by parent node */
      if (this.folders !== undefined && !(treeNode.parentIds.length === 0 || this.coreUtilities.arraysShareElements(parentIds, treeNode.parentIds.map(id => '' + id)))) {
        assigned = assigned.add(treeNode.id);
        continue;
      }
      if (!isNullOrUndefined(this.xAxis) && !isNullOrUndefined(this.xAxis.formFieldId) && this.xAxis.formFieldId !== '' && !isNullOrUndefined(this.yAxis) && !isNullOrUndefined(this.yAxis.formFieldId) && this.yAxis.formFieldId !== '') {
        const xy = this.getXY(undefined, this.xAxis, treeNode) + ':' + this.getXY(undefined, this.yAxis, treeNode);
        if (this.categoryMatrix.has(xy)) {
          assigned = assigned.add(treeNode.id);
          const category = this.categoryMatrix.get(xy);
          category.treeNodes.push(treeNode);
          category.treeNodes = category.treeNodes.sort((a, b) => a.sorting - b.sorting);
          category.initialTreeNodes = Object.assign([], category.treeNodes);
          const parentNodes = !isNullOrUndefined(this.parentNodeType) ? this.coreUtilities.getParents(treeNode.unfilteredParents, false).filter(parent => parent.nodeType === this.parentNodeType) : treeNode.parents;
          const count2 = parentNodes.length;
          for (let i2 = 0; i2 < count2; i2++) {
            const parent = this.coreUtilities.makeImmutable(parentNodes[i2]);
            if (!parents.has(parent.id)) {
              parent.unfilteredChildren = [];
            }
            parents = parents.set(parent.id, parent);
          }
        }
      }
    }

    const notAssigned = treeNodes.filter(treeNode => !assigned.has(treeNode.id));
    let ids = Set<string>();
    count = notAssigned.length;
    for (let i = 0; i < count; i++) {
      const notAssignedNode = notAssigned[i];
      ids = ids.add(notAssignedNode.id);
      const parentNodes = this.folders === undefined ? [] : !isNullOrUndefined(this.parentNodeType) ? this.coreUtilities.getParents(notAssignedNode.unfilteredParents, false).filter(parent => parent.nodeType === this.parentNodeType) : notAssignedNode.parents;
      const count2 = parentNodes.length;
      for (let i2 = 0; i2 < count2; i2++) {
        const parent = this.coreUtilities.makeImmutable(parentNodes[i2]);
        if (parents.has(parent.id)) {
          parent.unfilteredChildren.push(notAssignedNode);
        } else {
          parent.unfilteredChildren = [notAssignedNode];
        }
        parents = parents.set(parent.id, parent);
      }
    }
    const existingFolders = [];
    let notAssignedFolders = parents.filter(parent => this.modelId !== undefined && parent.modelId === this.modelId).map(parent => {
      existingFolders.push(parent.id);
      parent.children = parent.unfilteredChildren.filter(child => {
        if (ids.has(child.id)) {
          ids = ids.remove(child.id);
          return true;
        }
        return false;
      });
      return <TreeNode>{ id: parent.id, name: parent.name, children: parent.children };
    }).toArray();
    if (ids.size > 0) {
      notAssignedFolders.push(<TreeNode>{ id: '0', name: '', children: treeNodes.filter(treeNode => ids.has(treeNode.id)) });
    }
    /* Additional folders */
    if (this.folders !== undefined) {
      notAssignedFolders = notAssignedFolders.concat(this.folders.filter(folder => existingFolders.indexOf(folder.id) === -1).map(d => Map(d).set('children', []).toJS()));
    }
    this.notAssignedFolders = notAssignedFolders;
  }

  public getTreeNodeMaps(updates?: Map<string, Map<string, any>>): { treeNodesMap: Map<string, TreeNode>, updatesMap: Map<string, Map<string, any>> } {
    let treeNodes = Map<string, TreeNode>();
    /* Relationships */
    let relationshipsToCreate: string[] = [];
    let relationshipsToDelete: string[] = [];
    const relationshipsToStay = {};
    /* Iterate over matrix */
    let count = this.matrix.length;
    for (let i = 0; i < count; i++) {
      const row = this.matrix[i];
      const count2 = row.length;
      for (let i2 = 0; i2 < count2; i2++) {
        const cell = row[i2];
        const count3 = cell.treeNodes.length;
        for (let i3 = 0; i3 < count3; i3++) {
          const treeNode = cell.treeNodes[i3];
          const sorting = Math.ceil(((!isNullOrUndefined(cell.category) ? cell.category.sorting : 0) + parseFloat('0.' + i3)) * 10000);
          if (!isNullOrUndefined(updates)) {
            if (treeNode.sorting !== sorting) {
              updates = updates.set(treeNode.id, (updates.has(treeNode.id) ? updates.get(treeNode.id) : Map<string, any>()).set('sorting', sorting));
            }
            if (this.xAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const initial = Set<string>(!isNullOrUndefined(cell.initialTreeNodes) ? cell.initialTreeNodes.map(d => d.id) : []);
              const x = this.xAxis.formFieldId === 'dynamic-parents' ? cell.x.id + ':' + treeNode.id : treeNode.id + ':' + cell.x.id;
              if (!initial.has(treeNode.id)) {
                relationshipsToCreate.push(x);
              } else {
                relationshipsToStay[x] = true;
              }
            } else {
              if ('' + treeNode[this.xAxis.formFieldId] !== '' + cell.x.formId) {
                updates = updates.set(treeNode.id, (updates.has(treeNode.id) ? updates.get(treeNode.id) : Map<string, any>()).set(this.xAxis.formFieldId, cell.x.formId));
              }
            }
            if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const initial = Set<string>(!isNullOrUndefined(cell.initialTreeNodes) ? cell.initialTreeNodes.map(d => d.id) : []);
              const y = this.yAxis.formFieldId === 'dynamic-parents' ? cell.y.id + ':' + treeNode.id : treeNode.id + ':' + cell.y.id;
              if (!initial.has(treeNode.id)) {
                relationshipsToCreate.push(y);
              } else {
                relationshipsToStay[y] = true;
              }
            } else {
              if ('' + treeNode[this.yAxis.formFieldId] !== '' + cell.y.formId) {
                updates = updates.set(treeNode.id, (updates.has(treeNode.id) ? updates.get(treeNode.id) : Map<string, any>()).set(this.yAxis.formFieldId, cell.y.formId));
              }
            }
          } else {
            treeNode.sorting = sorting;
          }
          treeNodes = treeNodes.set(treeNode.id, treeNode);
        }
        if (count3 > 0 && !isNullOrUndefined(cell.initialTreeNodes) && cell.initialTreeNodes.length > 0) {
          let initial = Map<string, TreeNode>();
          let count4 = cell.initialTreeNodes.length;
          for (let i4 = 0; i4 < count4; i4++) {
            initial = initial.set(cell.initialTreeNodes[i4].id, cell.initialTreeNodes[i4]);
          }
          count4 = cell.treeNodes.length;
          for (let i4 = 0; i4 < count4; i4++) {
            initial = initial.remove(cell.treeNodes[i4].id);
          }
          initial.forEach(value => {
            if (this.xAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const x = this.xAxis.formFieldId === 'dynamic-parents' ? cell.x.id + ':' + value.id : value.id + ':' + cell.x.id;
              if (relationshipsToCreate.indexOf(x) !== -1) {
                relationshipsToCreate = relationshipsToCreate.filter(d => d !== x);
              } else {
                relationshipsToDelete.push(x);
              }
            }
            if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const y = this.yAxis.formFieldId === 'dynamic-parents' ? cell.y.id + ':' + value.id : value.id + ':' + cell.y.id;
              if (relationshipsToCreate.indexOf(y) !== -1) {
                relationshipsToCreate = relationshipsToCreate.filter(d => d !== y);
              } else {
                relationshipsToDelete.push(y);
              }
            }
          });
        }
        /* To delete */
        if (count3 === 0 && !isNullOrUndefined(cell.initialTreeNodes) && cell.initialTreeNodes.length > 0) {
          const count4 = cell.initialTreeNodes.length;
          for (let i4 = 0; i4 < count4; i4++) {
            const initialTreeNode = cell.initialTreeNodes[i4];
            if (this.xAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const x = this.xAxis.formFieldId === 'dynamic-parents' ? cell.x.id + ':' + initialTreeNode.id : initialTreeNode.id + ':' + cell.x.id;
              if (relationshipsToCreate.indexOf(x) !== -1) {
                relationshipsToCreate = relationshipsToCreate.filter(d => d !== x);
              } else {
                relationshipsToDelete.push(x);
              }
            }
            if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
              const y = this.yAxis.formFieldId === 'dynamic-parents' ? cell.y.id + ':' + initialTreeNode.id : initialTreeNode.id + ':' + cell.y.id;
              if (relationshipsToCreate.indexOf(y) !== -1) {
                relationshipsToCreate = relationshipsToCreate.filter(d => d !== y);
              } else {
                relationshipsToDelete.push(y);
              }
            }
          }
        }

        /* Clean up */
        const storeRelationshipsToCreate = [...relationshipsToCreate];
        relationshipsToCreate = relationshipsToCreate.filter(relationshipToCreate => {
          return relationshipsToDelete.indexOf(relationshipToCreate) === -1;
        });
        relationshipsToDelete = relationshipsToDelete.filter(relationshipToDelete => {
          return storeRelationshipsToCreate.indexOf(relationshipToDelete) === -1;
        });

        updates = updates.set('create-relationship', (updates.has('create-relationship') ? updates.get('create-relationship') : Map<string, any>()).set('create-relationship', relationshipsToCreate));
        updates = updates.set('delete-relationship', (updates.has('delete-relationship') ? updates.get('delete-relationship') : Map<string, any>()).set('delete-relationship', relationshipsToDelete));
      }
    }

    count = this.notAssignedFolders.length;
    for (let i = 0; i < count; i++) {
      const folder = this.notAssignedFolders[i];
      const count2 = folder.children.length;
      for (let i2 = 0; i2 < count2; i2++) {
        const treeNode = folder.children[i2];
        if (!isNullOrUndefined(updates)) {
          if ('' + treeNode[this.xAxis.formFieldId] !== '0' || '' + treeNode[this.yAxis.formFieldId] !== '0') {
            updates = updates.set(treeNode.id, Map<string, any>()
              .set(this.xAxis.formFieldId, '0')
              .set(this.yAxis.formFieldId, '0')
              .set('sorting', 0)
            );
          }
        } else {
          treeNode.sorting = 0;
        }
        treeNodes = treeNodes.set(treeNode.id, treeNode);
      }
    }
    return { treeNodesMap: treeNodes, updatesMap: updates };
  }

  public clearMatrix(draggedNode?: TreeNode, initialPosition?: string) {
    this.categoryMatrix = <Map<string, EisenhowerElement>>this.categoryMatrix.map(element => {
      if (isNullOrUndefined(draggedNode)) {
        element.treeNodes = [];
      } else {
        const rez = [];
        const coord = element.x.formId + ':' + element.y.formId;
        if (coord === initialPosition) {
          rez.push(draggedNode);
        } else {
          element.treeNodes.forEach(elem => {
            if (elem.id === draggedNode.id) {
              rez.push(elem);
            }
          });
        }

        element.treeNodes = rez;
      }
      return element;
    });
  }

  public getXY(axisNode: TreeNode, axisElement: TreeNode, treeNode: TreeNode) {
    /* The axis string */
    let axis: string;

    /* Structure */
    if (axisElement.nodeType === NODES_TYPE_STRUCTURE) {
      if (treeNode === undefined) {
        axis = axisNode.id;
      } else {
        const potentialNodeIds = Set<string>(axisElement.children.map(child => child.id));
        const nodes = treeNode[(axisElement.formFieldId === 'dynamic-parents' ? 'parents' : 'children')];
        if (nodes !== undefined) {
          const node = nodes.filter(d => potentialNodeIds.has(d.id))[0];
          if (node !== undefined) {
            axis = node.id;
          }
        }
      }
    }

    /* Return axis */
    return axis === undefined ? (treeNode === undefined ? axisNode.formId : treeNode[axisElement.formFieldId]) : axis;
  }

  public sortChildren(children: TreeNode[]): TreeNode[] {
    return children.sort((a, b) => {
      if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
        return a.name.localeCompare(b.name);
      } else {
        if (this.preSorting[a.id] !== undefined && this.preSorting[b.id] !== undefined) {
          const aPos = !!this.preSorting[a.id] ? this.preSorting[a.id] : 0;
          const bPos = !!this.preSorting[b.id] ? this.preSorting[b.id] : 0;
          if (aPos === 0) {
            return 1;
          }
          return aPos - bPos;
        } else {
          return a.positionX - b.positionX;
        }
      }
    }).sort((a, b) => {
      if (this.yAxis.nodeType === NODES_TYPE_STRUCTURE) {
        const aPos = !!this.preSorting[a.id] ? this.preSorting[a.id] : 0;
        const bPos = !!this.preSorting[b.id] ? this.preSorting[b.id] : 0;
        if (aPos === 0) {
          return 1;
        }
        return aPos - bPos;
      } else {
        return 0;
      }
    });
  }

}
